/**
 * Payroll Accounting Service
 * 
 * Handles automatic voucher creation when salary payments are processed.
 * Implements Flow D: HR + Payroll + Accounts integration.
 * 
 * When a salary payment is confirmed:
 * 1. Creates a Debit voucher for Salary Expense
 * 2. Creates a Credit voucher for Cash/Bank
 * 3. Both vouchers are linked via source_type='payroll_payment' and source_id=payment.id
 * 4. Idempotency is enforced at the database level via unique index
 */

import { supabase } from '../lib/supabase';
import { getTenantIdSync } from '../lib/tenantStore';
import type { PayrollPayment } from '../modules/payroll/types';

interface VoucherCreationResult {
  success: boolean;
  debitVoucherId?: string;
  creditVoucherId?: string;
  error?: string;
  isDuplicate?: boolean;
}

/**
 * Post salary payment vouchers to the accounting system
 * 
 * Creates two vouchers:
 * - Debit: Salary Expense (expense account)
 * - Credit: Cash/Bank (asset account)
 * 
 * @param payment The payroll payment record
 * @param employeeName Name of the employee for description
 * @param period Payment period (e.g., "January 2025")
 * @returns Result indicating success/failure and voucher IDs
 */
export async function postSalaryPaymentVouchers(
  payment: PayrollPayment,
  employeeName: string,
  period: string
): Promise<VoucherCreationResult> {
  try {
    const tenantId = getTenantIdSync();
    
    const salaryHeadId = await ensureVoucherHead(tenantId, 'Staff Salary', 'expense');
    const cashBankHeadId = await getCashOrBankHead(tenantId, payment.payment_method);
    
    if (!salaryHeadId || !cashBankHeadId) {
      return {
        success: false,
        error: 'Failed to resolve voucher heads for salary expense or cash/bank'
      };
    }
    
    const debitVoucherNumber = await generateVoucherNumber(tenantId, 'debit');
    const creditVoucherNumber = await generateVoucherNumber(tenantId, 'credit');
    
    const description = `Salary payment for ${employeeName} - ${period}`;
    
    const voucherDate = payment.payment_date;
    const sourceType = 'payroll_payment';
    const sourceId = payment.id;
    const createdBy = payment.created_by || 'system';
    
    const { data: debitVoucher, error: debitError } = await supabase
      .from('vouchers')
      .insert({
        tenant_id: tenantId,
        voucher_number: debitVoucherNumber,
        voucher_date: voucherDate,
        voucher_type: 'debit',
        voucher_head: 'Staff Salary',
        voucher_head_id: salaryHeadId,
        amount: payment.amount,
        payment_method: payment.payment_method,
        reference_number: payment.reference_number || null,
        description: description,
        source_type: sourceType,
        source_id: sourceId,
        status: 'approved', // Auto-approve payroll vouchers
        created_by: createdBy
      })
      .select('id')
      .single();
    
    if (debitError) {
      if (debitError.code === '23505') { // Unique constraint violation
        return {
          success: true,
          isDuplicate: true,
          error: 'Vouchers already exist for this payment'
        };
      }
      
      console.error('Error creating debit voucher:', debitError);
      return {
        success: false,
        error: `Failed to create debit voucher: ${debitError.message}`
      };
    }
    
    const creditHeadName = payment.payment_method === 'cash' ? 'Cash' : 'Bank';
    
    const { data: creditVoucher, error: creditError } = await supabase
      .from('vouchers')
      .insert({
        tenant_id: tenantId,
        voucher_number: creditVoucherNumber,
        voucher_date: voucherDate,
        voucher_type: 'credit',
        voucher_head: creditHeadName,
        voucher_head_id: cashBankHeadId,
        amount: payment.amount,
        payment_method: payment.payment_method,
        reference_number: payment.reference_number || null,
        description: description,
        source_type: sourceType,
        source_id: sourceId,
        status: 'approved', // Auto-approve payroll vouchers
        created_by: createdBy
      })
      .select('id')
      .single();
    
    if (creditError) {
      console.error('Error creating credit voucher:', creditError);
      
      if (debitVoucher?.id) {
        await supabase
          .from('vouchers')
          .delete()
          .eq('id', debitVoucher.id);
      }
      
      return {
        success: false,
        error: `Failed to create credit voucher: ${creditError.message}`
      };
    }
    
    return {
      success: true,
      debitVoucherId: debitVoucher?.id,
      creditVoucherId: creditVoucher?.id
    };
    
  } catch (error: any) {
    console.error('Error in postSalaryPaymentVouchers:', error);
    return {
      success: false,
      error: error.message || 'Unknown error occurred'
    };
  }
}

/**
 * Ensure a voucher head exists, creating it if necessary
 * 
 * @param tenantId Tenant ID
 * @param headName Name of the voucher head
 * @param headType Type of the head (expense, income, asset, liability)
 * @returns Voucher head ID or null if failed
 */
async function ensureVoucherHead(
  tenantId: string,
  headName: string,
  headType: 'expense' | 'income' | 'asset' | 'liability'
): Promise<string | null> {
  try {
    const { data: existingHead, error: selectError } = await supabase
      .from('voucher_heads')
      .select('id')
      .eq('tenant_id', tenantId)
      .eq('head_name', headName)
      .maybeSingle();
    
    if (selectError) {
      console.error('Error checking voucher head:', selectError);
      return null;
    }
    
    if (existingHead) {
      return existingHead.id;
    }
    
    const { data, error } = await supabase.rpc('ensure_voucher_head', {
      p_tenant_id: tenantId,
      p_head_name: headName,
      p_head_type: headType
    });
    
    if (error) {
      console.error('Error creating voucher head:', error);
      return null;
    }
    
    return data;
    
  } catch (error) {
    console.error('Error in ensureVoucherHead:', error);
    return null;
  }
}

/**
 * Get the Cash or Bank voucher head ID based on payment method
 * 
 * @param tenantId Tenant ID
 * @param paymentMethod Payment method (cash, bank_transfer, cheque)
 * @returns Voucher head ID or null if failed
 */
async function getCashOrBankHead(
  tenantId: string,
  paymentMethod: 'cash' | 'bank_transfer' | 'cheque'
): Promise<string | null> {
  const headName = paymentMethod === 'cash' ? 'Cash' : 'Bank';
  return ensureVoucherHead(tenantId, headName, 'asset');
}

/**
 * Generate the next voucher number for a tenant
 * 
 * @param tenantId Tenant ID
 * @param voucherType Type of voucher (debit or credit)
 * @returns Generated voucher number
 */
async function generateVoucherNumber(
  tenantId: string,
  voucherType: 'debit' | 'credit'
): Promise<string> {
  try {
    const { data, error } = await supabase.rpc('generate_voucher_number', {
      p_tenant_id: tenantId,
      p_voucher_type: voucherType
    });
    
    if (error) {
      console.error('Error generating voucher number:', error);
      const prefix = voucherType === 'debit' ? 'DV' : 'CV';
      const timestamp = Date.now();
      return `${prefix}-${timestamp}`;
    }
    
    return data;
    
  } catch (error) {
    console.error('Error in generateVoucherNumber:', error);
    const prefix = voucherType === 'debit' ? 'DV' : 'CV';
    const timestamp = Date.now();
    return `${prefix}-${timestamp}`;
  }
}

/**
 * Check if vouchers already exist for a payment
 * 
 * @param paymentId Payment ID
 * @returns True if vouchers exist, false otherwise
 */
export async function checkVouchersExist(paymentId: string): Promise<boolean> {
  try {
    const tenantId = getTenantIdSync();
    
    const { data, error } = await supabase
      .from('vouchers')
      .select('id')
      .eq('tenant_id', tenantId)
      .eq('source_type', 'payroll_payment')
      .eq('source_id', paymentId)
      .limit(1);
    
    if (error) {
      console.error('Error checking vouchers:', error);
      return false;
    }
    
    return (data && data.length > 0);
    
  } catch (error) {
    console.error('Error in checkVouchersExist:', error);
    return false;
  }
}

/**
 * Get vouchers for a specific payment
 * 
 * @param paymentId Payment ID
 * @returns Array of vouchers or empty array
 */
export async function getVouchersForPayment(paymentId: string): Promise<any[]> {
  try {
    const tenantId = getTenantIdSync();
    
    const { data, error } = await supabase
      .from('vouchers')
      .select('*')
      .eq('tenant_id', tenantId)
      .eq('source_type', 'payroll_payment')
      .eq('source_id', paymentId)
      .order('voucher_type', { ascending: true }); // debit first, then credit
    
    if (error) {
      console.error('Error fetching vouchers:', error);
      return [];
    }
    
    return data || [];
    
  } catch (error) {
    console.error('Error in getVouchersForPayment:', error);
    return [];
  }
}

/**
 * Seed default voucher heads for a tenant
 * This should be called when a new tenant is created
 * 
 * @param tenantId Tenant ID
 * @returns True if successful, false otherwise
 */
export async function seedDefaultVoucherHeads(tenantId: string): Promise<boolean> {
  try {
    const { error } = await supabase.rpc('seed_default_voucher_heads', {
      p_tenant_id: tenantId
    });
    
    if (error) {
      console.error('Error seeding voucher heads:', error);
      return false;
    }
    
    return true;
    
  } catch (error) {
    console.error('Error in seedDefaultVoucherHeads:', error);
    return false;
  }
}
